import { useEffect, useRef } from "react";

import { FormikProps } from "formik";
import classNames from "clsx";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

import { dischargeFilterInitialValues } from "@/utils/initialValues/dischargeFilter";
import { DischargeType } from "@/utils/enums/sintegrada/discharge/DischargeType";

import { FormattedControl } from "@/components/FormattedControl";
import { RedAsterisk } from "@/components/RedAsterisk";
import { AgeGroup } from "@/components/sintegrada/AgeGroup";
import { TransferUnitSelect } from "@/components/sintegrada/Selects/TransferUnitSelect";
import { GenderSelect } from "@/components/sintegrada/Selects/GenderSelect";
import { MedicalAttendanceDepartmentSelect } from "@/components/sintegrada/Selects/MedicalAttendanceDepartmentSelect";
import { DischargeTypeSelect } from "@/components/sintegrada/Selects/DischargeTypeSelect";
import { ApplyFilterButton } from "@/components/sintegrada/Buttons/ApplyFilterButton";
import { ExportExcelButton } from "@/components/sintegrada/Buttons/ExportExcelButton";

type Props = {
	formik: FormikProps<typeof dischargeFilterInitialValues>;
	handleExportExcel: () => void;
	disabledExportButton: boolean;
};

export function DischargeReportFilter({
	formik,
	handleExportExcel,
	disabledExportButton,
}: Props) {
	const { values, touched, errors } = formik;
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}

		formik.setFieldValue("transferUnit", {});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.dischargeType]);

	return (
		<Form
			className="mb-8"
			onSubmit={formik.handleSubmit}
		>
			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25">
					<Form.Label title="Data inicial" htmlFor="start-date">Data inicial <RedAsterisk /></Form.Label>

					<FormattedControl
						id="start-date"
						type="datetime-local"
						placeholder="Data inicial"
						{...formik.getFieldProps("startDate")}
					/>

					{touched.startDate && errors.startDate && (
						<span className="text-danger">{errors.startDate}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Data final" htmlFor="end-date">Data final <RedAsterisk /></Form.Label>

					<FormattedControl
						id="end-date"
						type="datetime-local"
						placeholder="Data final"
						{...formik.getFieldProps("endDate")}
					/>

					{touched.endDate && errors.endDate && (
						<span className="text-danger">{errors.endDate}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Setor" htmlFor="internal-department">Setor <RedAsterisk /></Form.Label>
				
					<MedicalAttendanceDepartmentSelect
						inputId="internal-department"
						value={values.department}
						onChange={(option) => formik.setFieldValue("department", option)}
					/>

					{touched.department && errors.department && (
						<span className="text-danger">{errors.department.label}</span>
					)}
				</div>

				<div className="w-25">
					<Form.Label title="Tipo de alta" htmlFor="discharge-type">Tipo de alta <RedAsterisk /></Form.Label>

					<DischargeTypeSelect
						inputId="discharge-type"
						value={values.dischargeType}
						onChange={(option) => formik.setFieldValue("dischargeType", option)}
					/>

					{touched.dischargeType && errors.dischargeType && (
						<span className="text-danger">{errors.dischargeType.label}</span>
					)}
				</div>
			</Col>

			<Col md={12} className="d-flex gap-4 mb-4">
				{values.dischargeType.value === DischargeType.DischargeByTransfer && (
					<div className="w-25">
						<Form.Label title="Unidades de transferencias" htmlFor="transfer-units">
							Unidades de transferencias <RedAsterisk />
						</Form.Label>

						<TransferUnitSelect
							value={values.transferUnit.value ? { label: values.transferUnit.label, value: values.transferUnit.value } : null}
							onChange={(option) => formik.setFieldValue("transferUnit", option)}
						/>

						{touched.transferUnit && errors.transferUnit && (
							<span className="text-danger">{errors.transferUnit.label}</span>
						)}
					</div>
				)}
			
				<div className="w-25 d-flex gap-4">
					<div className="w-50">
						<Form.Label title="Gênero" htmlFor="gender">Gênero</Form.Label>

						<GenderSelect
							value={values.gender}
							onChange={(option) => formik.setFieldValue("gender", option)}
						/>
					</div>

					<div className={classNames("w-50 d-flex gap-4", values.dischargeType.value !== DischargeType.DischargeByTransfer && "pe-1")}>
						<AgeGroup
							minAge={String(values.min_age)}
							maxAge={String(values.max_age)}
							handleChangeMinAge={(value) => formik.setFieldValue("min_age", value)}
							handleChangeMaxAge={(value) => formik.setFieldValue("max_age", value)}
						/>
					</div>
				</div>

				<div className={classNames("w-50 my-8 ms-4 d-flex align-items-end gap-4", values.dischargeType.value === DischargeType.DischargeByTransfer ? "w-50" : "w-75")}>
					<div className="me-4 my-2">
						<Form.Check
							id="individual"
							type="checkbox"
							label="Individual"
							{...formik.getFieldProps("individual")}
						/>
					</div>

					<ApplyFilterButton type="submit" disabled={formik.isSubmitting || !formik.isValid} />

					<ExportExcelButton
						onClick={handleExportExcel}
						disabledExportButton={disabledExportButton}
					/>
				</div>
			</Col>
		</Form>
	);
}
